import { useEffect, useRef, useState } from 'react';
import { format, isSameDay } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Loader2, MessageSquare, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/app/providers/AuthProvider';
import { useCurrentClient } from '@/hooks/useCurrentClient';
import { useMessages, useSendMessage } from './useMessages';

const ROLE_LABELS: Record<string, string> = {
  admin_clinance: 'Administrador',
  gerente_operacao: 'Gerente',
  consultor: 'Consultor',
  comercial: 'Comercial',
  cliente: 'Cliente',
};

export function MessagesPage() {
  const { clientId, client } = useCurrentClient();
  const { user, profile } = useAuth();
  const { data: messages = [], isLoading } = useMessages();
  const send = useSendMessage();
  const [body, setBody] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Rola para o final ao carregar / nova mensagem
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages.length]);

  const handleSend = async () => {
    if (!body.trim()) return;
    try {
      await send.mutateAsync(body);
      setBody('');
    } catch {
      // toast já mostrou
    }
  };

  if (!clientId) {
    return (
      <Card>
        <CardContent className="p-12 text-center text-muted-foreground">
          <MessageSquare className="mx-auto mb-3 h-10 w-10 opacity-40" />
          <p className="text-sm">Selecione um cliente no topo para abrir o chat.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="flex h-[calc(100vh-8rem)] flex-col gap-4">
      <div>
        <h1 className="font-brand text-2xl font-bold text-foreground">Mensagens</h1>
        <p className="text-sm text-muted-foreground">
          {client?.name ? `${client.name} · ` : ''}conversa com a consultoria
        </p>
      </div>

      <Card className="flex flex-1 flex-col overflow-hidden">
        <CardContent className="flex flex-1 flex-col gap-3 p-4">
          {isLoading ? (
            <div className="flex flex-1 items-center justify-center">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : messages.length === 0 ? (
            <div className="flex flex-1 flex-col items-center justify-center text-center text-muted-foreground">
              <MessageSquare className="mb-3 h-10 w-10 opacity-40" />
              <p className="text-sm font-medium text-foreground">Sem mensagens ainda.</p>
              <p className="text-xs">Envie a primeira mensagem para iniciar a conversa.</p>
            </div>
          ) : (
            <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto pr-2">
              {messages.map((m, idx) => {
                const isMine = m.sender_user_id === user?.id;
                const previous = messages[idx - 1];
                const showDateHeader =
                  !previous || !isSameDay(new Date(m.created_at), new Date(previous.created_at));
                const roleLabel = ROLE_LABELS[m.sender_role] ?? m.sender_role;
                return (
                  <div key={m.id}>
                    {showDateHeader && (
                      <div className="my-2 flex items-center gap-2">
                        <div className="h-px flex-1 bg-border" />
                        <span className="text-xs font-medium text-muted-foreground">
                          {format(new Date(m.created_at), "EEEE, dd 'de' MMM", { locale: ptBR })}
                        </span>
                        <div className="h-px flex-1 bg-border" />
                      </div>
                    )}
                    <div className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}>
                      <div
                        className={`max-w-[75%] rounded-lg px-3 py-2 ${
                          isMine
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-secondary text-foreground'
                        }`}
                      >
                        {!isMine && (
                          <p className="text-xs font-semibold">
                            {m.sender_name}
                            <span
                              className={`ml-2 rounded-full px-1.5 py-0.5 text-xs font-medium ${
                                m.sender_role === 'cliente'
                                  ? 'bg-white/20'
                                  : 'bg-primary/20 text-primary'
                              }`}
                            >
                              {roleLabel}
                            </span>
                          </p>
                        )}
                        <p className="mt-0.5 whitespace-pre-wrap text-sm">{m.body}</p>
                        <p
                          className={`mt-1 text-right text-[10px] ${
                            isMine ? 'text-primary-foreground/70' : 'text-muted-foreground'
                          }`}
                        >
                          {format(new Date(m.created_at), 'HH:mm', { locale: ptBR })}
                          {isMine && m.read_at && ' · lida'}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Composer */}
          <div className="border-t border-border pt-3">
            <div className="flex items-end gap-2">
              <Textarea
                rows={2}
                placeholder={`Mensagem como ${profile?.name ?? 'você'}…`}
                value={body}
                onChange={(e) => setBody(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    void handleSend();
                  }
                }}
                disabled={send.isPending}
                className="resize-none"
              />
              <Button
                onClick={handleSend}
                disabled={!body.trim() || send.isPending}
                className="bg-primary text-primary-foreground hover:bg-primary/90"
              >
                {send.isPending ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <Send size={14} />
                )}
              </Button>
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              Enter envia · Shift+Enter quebra linha
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
